import type { CitationOutput } from "@/components/tool-kit/source-citation-builder";
import {
  type CitationParts,
  type PartialDate,
  parsePartialDate,
  tidyParts,
} from "@/lib/tool-kit/citation-parts";
import {
  dayMonthYear,
  get,
  italic,
  type LegalValues,
  plain,
} from "@/lib/tool-kit/legal-sources/common";

/*
 * EU legislation and Court of Justice judgments in OSCOLA, APA 7 and Harvard (Cite Them
 * Right).
 *
 * Sources checked (October 2026):
 * - OSCOLA 4th ed. §2.6 (Oxford, 2012) and the OSCOLA 5th ed. (2026) key changes and
 *   UWE/ULaw summaries: "legislation type | number | title | [year] | OJ L issue/first
 *   page" — "Council Directive 2002/60/EC of 27 June 2002 laying down … [2002] OJ
 *   L192/27." Pinpoints follow the OJ reference and a comma ("art 17"); a short form
 *   goes in brackets ("('Maastricht Treaty')" in OSCOLA 5). The table of legislation
 *   repeats the citation without the full stop or pinpoint. Judgments (OSCOLA 5
 *   §4.4.2): "Case C-403/03 Schempp v Finanzamt EU:C:2005:446 [19]." — case name in
 *   italics, the ECLI instead of a report, paragraph pinpoints in square brackets.
 *   Post-2015 act numbers take the form (domain) YYYY/N (Oxford OSCOLA FAQ).
 * - Acts published in the Official Journal L series since 1 October 2023 have no issue
 *   or page; the EU's own reference is "OJ L, 2024/1689, 12.7.2024" (EUR-Lex). OSCOLA's
 *   printed examples predate that, so this generator writes "[2024] OJ L 2024/1689" —
 *   the EU reference with OSCOLA's minimal punctuation — and says so on the page.
 * - Cite Them Right (Harvard), as reproduced verbatim by Robert Gordon University and
 *   City St George's: legislation — "'Legislation type number and title' (Year)
 *   Official Journal (OJ) series Issue, page numbers or URL (Accessed: date)", e.g.
 *   "'Regulation (EU) 2016/679 on the protection of natural persons …' Official Journal
 *   L119, pp. 1-88."; in text "(Regulation (EU) 2016/679)". Judgments — "'Case name'
 *   (Year) case number; ECLI number. Publication title, section, page numbers or URL
 *   (Accessed: date)"; in text "(Case name, Year)".
 * - APA 7 has no EU template (Copenhagen Business School library: "the official APA
 *   Manual only give guidance and examples for US legal materials"). APA's legal
 *   chapter orders references title, source, date (Table 11.1, §11.3), so the act's
 *   official title is followed by the EU's Official Journal reference and the year.
 *   Case names are roman in the reference list and italic in text (§11.4).
 * - Facts for the page examples (EUR-Lex): Regulation (EU) 2016/679, OJ L 119,
 *   4.5.2016, pp. 1–88; Case C-311/18, Data Protection Commissioner v Facebook Ireland
 *   Limited and Maximillian Schrems, ECLI:EU:C:2020:559, judgment of 16 July 2020.
 */

export const EU_LEGISLATION_FIELDS = {
  designation: "designation",
  institutions: "institutions",
  adopted: "adopted",
  subject: "subject",
  oj: "oj",
  article: "article",
  shortTitle: "shortTitle",
  url: "url",
  accessed: "accessed",
} as const;

export const EU_CASE_FIELDS = {
  caseNumber: "caseNumber",
  caseName: "caseName",
  ecli: "ecli",
  date: "date",
  paragraph: "paragraph",
  url: "url",
  accessed: "accessed",
} as const;

export interface EuLawLabels {
  oscola: string;
  apa: string;
  harvard: string;
  footnote: string;
  tableOfLegislation: string;
  tableOfCases: string;
  referenceList: string;
  parenthetical: string;
  narrative: string;
  inText: string;
  oscolaNote: string;
  oscolaActByActNote: string;
  apaNote: string;
  harvardNote: string;
  oscolaCaseNote: string;
  apaCaseNote: string;
  harvardCaseNote: string;
  ojUnreadable: string;
}

/** An Official Journal reference: "L 119, 4.5.2016, p. 1–88" or "L, 2024/1689, 12.7.2024". */
export type OjReference =
  | {
      kind: "issue";
      series: string;
      issue: string;
      firstPage: string;
      lastPage: string;
      date: PartialDate;
    }
  | { kind: "act"; series: string; number: string; date: PartialDate };

const RE_OJ_ISSUE =
  /^(?:OJ\s*)?([LC])\s*(\d+)\s*,\s*(\d{1,2})\.(\d{1,2})\.(\d{4})\s*,\s*pp?\.\s*(\d+)(?:\s*[-–]\s*(\d+))?/i;
const RE_OJ_ACT =
  /^(?:OJ\s*)?([LC])\s*,\s*(\d{4}\/\d+)\s*,\s*(\d{1,2})\.(\d{1,2})\.(\d{4})/i;

/** Read the reference as EUR-Lex prints it. Null when it is in neither form. */
export function parseOjReference(input: string): OjReference | null {
  const text = input.trim();
  const issue = RE_OJ_ISSUE.exec(text);
  if (issue) {
    return {
      kind: "issue",
      series: issue[1].toUpperCase(),
      issue: issue[2],
      date: {
        year: Number(issue[5]),
        month: Number(issue[4]),
        day: Number(issue[3]),
      },
      firstPage: issue[6],
      lastPage: issue[7] ?? "",
    };
  }
  const act = RE_OJ_ACT.exec(text);
  if (act) {
    return {
      kind: "act",
      series: act[1].toUpperCase(),
      number: act[2],
      date: {
        year: Number(act[5]),
        month: Number(act[4]),
        day: Number(act[3]),
      },
    };
  }
  return null;
}

/* --------------------------------------------------------------- legislation */

interface Act {
  designation: string;
  institutions: string;
  adopted: PartialDate | null;
  subject: string;
  oj: OjReference | null;
  article: string;
  shortTitle: string;
  url: string;
  accessed: PartialDate | null;
}

const RE_ARTICLE_PREFIX = /^(arts?\.?|articles?)\s*/i;

function readAct(values: LegalValues): Act {
  const F = EU_LEGISLATION_FIELDS;
  return {
    designation: get(values, F.designation),
    institutions: get(values, F.institutions),
    adopted: parsePartialDate(get(values, F.adopted)),
    subject: get(values, F.subject),
    oj: parseOjReference(get(values, F.oj)),
    article: get(values, F.article).replace(RE_ARTICLE_PREFIX, ""),
    shortTitle: get(values, F.shortTitle),
    url: get(values, F.url),
    accessed: parsePartialDate(get(values, F.accessed)),
  };
}

/** The act's full title as the Official Journal prints it. */
function officialTitle(act: Act): string {
  const adopted = act.adopted ? `of ${dayMonthYear(act.adopted)}` : "";
  return [act.designation, act.institutions, adopted, act.subject]
    .filter(Boolean)
    .join(" ");
}

/** OSCOLA: "[2016] OJ L119/1" or "[2024] OJ L 2024/1689". */
function oscolaOj(oj: OjReference): string {
  return oj.kind === "issue"
    ? `[${oj.date.year}] OJ ${oj.series}${oj.issue}/${oj.firstPage}`
    : `[${oj.date.year}] OJ ${oj.series} ${oj.number}`;
}

/** The EU's own short form: "OJ L 119, p. 1" or "OJ L, 2024/1689". */
function euOj(oj: OjReference): string {
  return oj.kind === "issue"
    ? `OJ ${oj.series} ${oj.issue}, p. ${oj.firstPage}`
    : `OJ ${oj.series}, ${oj.number}`;
}

function accessedNote(url: string, accessed: PartialDate | null): string {
  if (url === "") {
    return "";
  }
  const when = accessed ? ` (Accessed: ${dayMonthYear(accessed)})` : "";
  return ` Available at: ${url}${when}.`;
}

function unreadable(labels: EuLawLabels): CitationOutput[] {
  return [
    {
      key: "oscola",
      label: labels.oscola,
      entries: [],
      note: labels.ojUnreadable,
    },
    { key: "apa", label: labels.apa, entries: [], note: labels.ojUnreadable },
    {
      key: "harvard",
      label: labels.harvard,
      entries: [],
      note: labels.ojUnreadable,
    },
  ];
}

function oscolaLegislation(
  act: Act,
  oj: OjReference,
  labels: EuLawLabels
): CitationOutput {
  const base = `${officialTitle(act)} ${oscolaOj(oj)}`;
  const short = act.shortTitle ? ` (‘${act.shortTitle}’)` : "";
  const pinpoint = act.article ? `, art ${act.article}` : "";
  return {
    key: "oscola",
    label: labels.oscola,
    entries: [
      { label: labels.footnote, parts: [plain(`${base}${short}${pinpoint}.`)] },
      { label: labels.tableOfLegislation, parts: [plain(base)] },
    ],
    note: oj.kind === "act" ? labels.oscolaActByActNote : labels.oscolaNote,
  };
}

function apaLegislation(
  act: Act,
  oj: OjReference,
  labels: EuLawLabels
): CitationOutput {
  const year = String(oj.date.year);
  const reference = `${officialTitle(act)}, ${euOj(oj)} (${year}).${
    act.url ? ` ${act.url}` : ""
  }`;
  return {
    key: "apa",
    label: labels.apa,
    entries: [
      { label: labels.referenceList, parts: [plain(reference)] },
      {
        label: labels.parenthetical,
        parts: [plain(`(${act.designation}, ${year})`)],
      },
      {
        label: labels.narrative,
        parts: [plain(`${act.designation} (${year})`)],
      },
    ],
    note: labels.apaNote,
  };
}

function harvardLegislation(
  act: Act,
  oj: OjReference,
  labels: EuLawLabels
): CitationOutput {
  const title = [act.designation, act.subject].filter(Boolean).join(" ");
  let location: string;
  if (oj.kind === "issue") {
    const pages = oj.lastPage
      ? `pp. ${oj.firstPage}–${oj.lastPage}`
      : `p. ${oj.firstPage}`;
    location = ` ${oj.series}${oj.issue}, ${pages}.`;
  } else {
    location = ` ${oj.series}, ${oj.number}.${accessedNote(act.url, act.accessed)}`;
  }
  const parts: CitationParts = [
    plain(`‘${title}’ (${oj.date.year}) `),
    italic("Official Journal"),
    plain(location),
  ];
  return {
    key: "harvard",
    label: labels.harvard,
    entries: [
      { label: labels.referenceList, parts: tidyParts(parts) },
      { label: labels.inText, parts: [plain(`(${act.designation})`)] },
    ],
    note: labels.harvardNote,
  };
}

export function euLegislationIsReady(values: LegalValues): boolean {
  const F = EU_LEGISLATION_FIELDS;
  return get(values, F.designation) !== "" && get(values, F.oj) !== "";
}

export function formatEuLegislation(
  values: LegalValues,
  labels: EuLawLabels
): CitationOutput[] {
  const act = readAct(values);
  const oj = act.oj;
  if (!oj) {
    return unreadable(labels);
  }
  return [
    oscolaLegislation(act, oj, labels),
    apaLegislation(act, oj, labels),
    harvardLegislation(act, oj, labels),
  ];
}

/* --------------------------------------------------------------------- cases */

const RE_CASE_PREFIX = /^case\s+/i;
const RE_ECLI_PREFIX = /^ECLI:/i;

export function euCaseIsReady(values: LegalValues): boolean {
  const F = EU_CASE_FIELDS;
  return (
    get(values, F.caseNumber) !== "" &&
    get(values, F.caseName) !== "" &&
    parsePartialDate(get(values, F.date)) !== null
  );
}

export function formatEuCase(
  values: LegalValues,
  labels: EuLawLabels
): CitationOutput[] {
  const F = EU_CASE_FIELDS;
  const number = get(values, F.caseNumber).replace(RE_CASE_PREFIX, "");
  const name = get(values, F.caseName);
  const rawEcli = get(values, F.ecli).replace(RE_ECLI_PREFIX, "");
  const ecli = rawEcli ? `ECLI:${rawEcli}` : "";
  const date = parsePartialDate(get(values, F.date));
  const year = date ? String(date.year) : "n.d.";
  const paragraph = get(values, F.paragraph);
  const url = get(values, F.url);

  const oscola: CitationParts = [
    plain(`Case ${number} `),
    italic(name),
    plain(rawEcli ? ` ${rawEcli}` : ""),
  ];
  const apaReference = `${[name, `Case ${number}`, ecli].filter(Boolean).join(", ")} (${year}).${
    url ? ` ${url}` : ""
  }`;
  const harvard = `‘${name}’ (${year}) Case no. ${number}${ecli ? `; ${ecli}` : ""}.${accessedNote(
    url,
    parsePartialDate(get(values, F.accessed))
  )}`;

  return [
    {
      key: "oscola",
      label: labels.oscola,
      entries: [
        {
          label: labels.footnote,
          parts: tidyParts([
            ...oscola,
            plain(paragraph ? ` [${paragraph}].` : "."),
          ]),
        },
        { label: labels.tableOfCases, parts: tidyParts(oscola) },
      ],
      note: labels.oscolaCaseNote,
    },
    {
      key: "apa",
      label: labels.apa,
      entries: [
        { label: labels.referenceList, parts: [plain(apaReference)] },
        {
          label: labels.parenthetical,
          parts: [plain("("), italic(name), plain(`, ${year})`)],
        },
        {
          label: labels.narrative,
          parts: [italic(name), plain(` (${year})`)],
        },
      ],
      note: labels.apaCaseNote,
    },
    {
      key: "harvard",
      label: labels.harvard,
      entries: [
        { label: labels.referenceList, parts: [plain(harvard)] },
        { label: labels.inText, parts: [plain(`(${name}, ${year})`)] },
      ],
      note: labels.harvardCaseNote,
    },
  ];
}
